import { useEffect, useState } from "react";

// A page with many games fires many simultaneous cross-origin logo requests at once; on a
// slower connection some can hit a transient timeout/connection-reset even though the CDN URL
// itself is valid and reachable. Retry a couple of times (forcing a fresh <img> via `key`, since
// re-setting an unchanged `src` on the same element doesn't reliably re-fetch) before treating
// the logo as genuinely missing/broken.
const LOGO_MAX_RETRIES = 2;

// Shared across game cards, game detail, Largest Leans and the game explorer -- honest
// abbreviation fallback (never a fabricated logo) whenever a team has no verified logo_url.
// `ringColor`, when passed (NFL cards only, via nflTeams.js's per-team brand color), adds a
// colored ring so each of 32 teams reads as visually distinct rather than sitting in the same
// plain white circle -- entirely optional, every other caller's look is unchanged.
export const TeamLogo = ({ team, size = 24, testId, ringColor }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [team?.logo_url]);

  const showFallback = !team?.logo_url || failed;

  const handleError = () => {
    setAttempt((prev) => {
      const next = prev + 1;
      if (next > LOGO_MAX_RETRIES) setFailed(true);
      return next;
    });
  };

  return (
    <span
      data-testid={testId}
      style={{
        height: size,
        width: size,
        boxShadow: ringColor ? `0 0 0 1.5px ${ringColor}, 0 0 6px 0 ${ringColor}66` : undefined,
      }}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5"
    >
      {showFallback ? (
        <span className="font-mono text-[9px] uppercase text-zinc-700">{team?.abbreviation?.slice(0, 2) || "?"}</span>
      ) : (
        <img
          key={attempt}
          src={team.logo_url}
          alt={`${team.name || "Team"} logo`}
          className="h-full w-full object-contain"
          onError={handleError}
        />
      )}
    </span>
  );
};
